// ══════════════════════════════════════════════════════════════════════════════════════════════
//  LOS IDIOMAS DE LA APLICACIÓN — t('clave', 'Texto') (B-2259, t523, 27-oct-2026)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  EL DUEÑO, 27-oct: «queremos traducir la totalidad del sistema a varios idiomas, ya que hay clínicas y/o doctores que
//  hablan otros idiomas». Cómo está montado, convenciones de claves y cómo se añade un idioma: docs/IDIOMAS-PLAN.md.
//
//  CÓMO SE ESCRIBE UN TEXTO QUE SE VE (en cualquier pantalla, desde hoy):
//
//      import { t, tx } from '../utils/i18n'
//      <h1>{t('agenda.titulo', 'Agenda del día')}</h1>
//      <input placeholder={t('agenda.buscar', 'Buscar paciente')} />
//      toast(t('agenda.guardada', 'Cita guardada'))
//      <p>{tx('agenda.resumen', 'Hola {{nombre}}, tienes {{n}} citas', { nombre, n })}</p>
//
//  · El castellano va EN EL CÓDIGO, como segundo argumento: en castellano, y con la función «idiomas» apagada, sale
//    exactamente lo que ponía antes. Lo vigila backend/services/i18n-castellano-igual.test.js.
//  · t() devuelve SIEMPRE un texto (para atributos, toasts, títulos). tx() es para los hijos de un elemento: si un hueco
//    es un elemento de React o un `cond && …`, devuelve los trozos y React los pinta igual que antes.
//  · Las claves: «zona.Fichero.lo_que_dice» (la herramienta herramientas/i18n/extraer.js las pone solas). Una clave no
//    se cambia nunca; si cambias el texto, la traducción vieja deja de enseñarse sola (utils/i18nMotor: «desfasada»).
//  · NO se llama a t() fuera de una función (en una constante de módulo): se evaluaría al cargar el fichero, antes de que
//    llegue el diccionario, y saldría en castellano para siempre.
//
//  QUIÉN PONE EL IDIOMA: components/idiomas/IdiomaDeLaSesion.jsx (el equipo, con su perfil o el de la clínica) y cada
//  pantalla del paciente (utils/i18nCargar.js · cargarIdioma). Cuando cambia, components/idiomas/ConIdioma vuelve a montar
//  la aplicación para que todo se pinte en el nuevo. El tipo de negocio (fisioterapia, podología...) lo pone
//  ponerTipoNegocio: sus variantes están en shared/i18n/es/vocabulario.json («clave@tipo»).
//
//  SIN REACT, SIN RED, SIN import.meta: lo cargan de verdad las guardias (test-helpers/utilDelFrontend) y cualquier pantalla.
import { traducir, traducirEnTrozos, capaDe, normalizarIdioma as normalizarConCatalogo, RESERVA, huella } from './i18nMotor.js'
import CATALOGO from '../../../shared/i18n/idiomas.json' with { type: 'json' }
import VOCABULARIO_ES from '../../../shared/i18n/es/vocabulario.json' with { type: 'json' }

const CAPA_ES = capaDe(RESERVA, [VOCABULARIO_ES])
export const IDIOMAS = Object.freeze((CATALOGO && Array.isArray(CATALOGO.idiomas) ? CATALOGO.idiomas : []).map((i) => Object.freeze({ ...i })))
const POR_CODIGO = new Map(IDIOMAS.map((i) => [i.codigo, i]))
const ELEGIBLES = new Set(['base', 'en_curso', 'completo'])

const firmaDe = (e) => e.idioma + '|' + (e.tipo || '') + '|' + e.capas.map((c) => Object.keys(c.textos || {}).length).join(',')
let _estado = { idioma: RESERVA, tipo: null, capas: [CAPA_ES] }
let _firma = firmaDe(_estado)
let _version = 0
const _oyentes = new Set()

/** t('clave', 'Texto en castellano', { huecos }) → texto en el idioma activo (o el castellano). */
export function t(clave, porDefecto, valores) {
  return traducir(_estado, clave, porDefecto, valores)
}

/** tx(): como t(), para los hijos de JSX: con un hueco que no es texto, devuelve los trozos. */
export function tx(clave, porDefecto, valores) {
  return traducirEnTrozos(_estado, clave, porDefecto, valores)
}

export function datosIdioma(codigo) {
  return POR_CODIGO.get(String(codigo || '')) || null
}

/** Los que se pueden elegir hoy: el castellano y los que tienen su traducción en marcha o completa. */
export function idiomasElegibles() {
  return IDIOMAS.filter((i) => ELEGIBLES.has(i.estado))
}

export function esElegible(codigo) {
  const i = datosIdioma(codigo)
  return !!i && ELEGIBLES.has(i.estado)
}

export const direccionDe = (codigo) => ((datosIdioma(codigo) || {}).dir === 'rtl' ? 'rtl' : 'ltr')
export const localeDe = (codigo) => (datosIdioma(codigo) || datosIdioma(RESERVA) || { locale: 'es-ES' }).locale
export const idiomaActivo = () => _estado.idioma
export const tipoNegocioActivo = () => _estado.tipo
export const versionIdioma = () => _version

export function suscribirIdioma(fn) {
  _oyentes.add(fn)
  return () => { _oyentes.delete(fn) }
}

function _cambiar(nuevo) {
  const firma = firmaDe(nuevo)
  if (firma === _firma) return false
  _estado = nuevo
  _firma = firma
  _version++
  for (const f of [..._oyentes]) { try { f(_version) } catch (_) { /* un oyente roto no para a los demás */ } }
  return true
}

/**
 * Pone el idioma activo con sus diccionarios (los JSON de shared/i18n/<idioma>/*.json, ya cargados). Un idioma que no
 * está en el catálogo, o el castellano, deja el castellano. Devuelve true si ha cambiado algo.
 */
export function ponerIdioma(idioma, diccionarios = []) {
  const codigo = POR_CODIGO.has(String(idioma || '')) ? String(idioma) : RESERVA
  const capas = codigo === RESERVA ? [CAPA_ES] : [capaDe(codigo, diccionarios), CAPA_ES]
  const cambio = _cambiar({ idioma: codigo, tipo: _estado.tipo, capas })
  if (cambio) _marcarDocumento(codigo)
  return cambio
}

/** El tipo de negocio de la clínica (fisioterapia, podologia...): sus variantes «clave@tipo». null = el de siempre. */
export function ponerTipoNegocio(tipo) {
  const limpio = typeof tipo === 'string' && /^[a-z0-9-]{1,40}$/.test(tipo) ? tipo : null
  return _cambiar({ ..._estado, tipo: limpio })
}

function _marcarDocumento(codigo) {
  try {
    if (typeof document === 'undefined' || !document.documentElement) return
    const html = document.documentElement
    html.setAttribute('lang', codigo)
    if (direccionDe(codigo) === 'rtl') html.setAttribute('dir', 'rtl')
    else html.removeAttribute('dir')
  } catch (_) { /* sin documento */ }
}

// Los textos del diccionario del idioma activo (para que components/BotonTraducir no vuelva a traducir con la IA lo que ya
// sale traducido). Se hace la primera vez que se pregunta y se rehace al cambiar de idioma.
let _textosActivos = null
let _textosVersion = -1
/** ¿Es este texto (tal cual se ve) una traducción del diccionario del idioma activo? En castellano, nunca. */
export function esTextoTraducido(texto) {
  if (_estado.idioma === RESERVA) return false
  if (_textosVersion !== _version) {
    _textosActivos = new Set()
    for (const c of _estado.capas) {
      if (!c || c.idioma === RESERVA) continue
      for (const v of Object.values(c.textos || {})) if (typeof v === 'string' && v.trim()) _textosActivos.add(v.trim())
    }
    _textosVersion = _version
  }
  return _textosActivos.has(String(texto == null ? '' : texto).trim())
}

// UN TEXTO DE FUERA DE UN COMPONENTE (una constante del módulo: la leyenda del odontograma, una lista de opciones). Al cargar
// el fichero todavía no hay idioma, así que no se traduce aquí: marca() lo devuelve tal cual y deja la clave para el
// diccionario (la herramienta la recoge como un t()). Donde se PINTA, tDato(texto) lo enseña en el idioma activo.
export function marca(clave, texto) { return texto }

// UN TEXTO EN CASTELLANO QUE VIENE DE UN DATO: el nombre de fábrica de un estado de cita, de un tratamiento o de una
// pregunta de la historia de salud (shared/i18n/es/catalogos.json, herramientas/i18n/catalogos.js), o una constante
// marcada con marca(). Si algún diccionario del idioma activo tiene ese mismo castellano (por su huella), su traducción; si
// no —lo escribió la clínica—, tal cual. En castellano, siempre tal cual. No cambia nunca el dato: solo lo que se ve.
let _porHuellaUno = null
let _porHuellaUnoVersion = -1
export function tDato(texto) {
  if (_estado.idioma === RESERVA || texto == null || texto === '') return texto
  if (_porHuellaUnoVersion !== _version) {
    _porHuellaUno = new Map()
    for (const c of _estado.capas) {
      if (!c || c.idioma === RESERVA) continue
      for (const [k, h] of Object.entries(c.fuentes || {})) {
        const v = c.textos && c.textos[k]
        if (typeof v === 'string' && v.trim() && !_porHuellaUno.has(h)) _porHuellaUno.set(h, v)
      }
    }
    _porHuellaUnoVersion = _version
  }
  const s = String(texto)
  const v = _porHuellaUno.get(huella(s)) ?? _porHuellaUno.get(huella(s.trim()))
  return v === undefined ? texto : v
}

// Lo que se ve, en el idioma activo, de un texto escrito en castellano: las traducciones de las claves cuyo castellano es
// ese (por su huella). Para quien busca un elemento de la pantalla por lo que dice (components/GuiaDePantalla: el globo del
// botón «+ Crear» tiene que encontrarlo también cuando dice «+ Create»). En castellano, ninguna.
let _porHuella = null
let _porHuellaVersion = -1
export function traduccionesDe(textoCastellano) {
  if (_estado.idioma === RESERVA) return []
  if (_porHuellaVersion !== _version) {
    _porHuella = new Map()
    for (const c of _estado.capas) {
      if (!c || c.idioma === RESERVA) continue
      for (const [k, h] of Object.entries(c.fuentes || {})) {
        const v = c.textos && c.textos[k]
        if (typeof v !== 'string' || !v.trim()) continue
        if (!_porHuella.has(h)) _porHuella.set(h, new Set())
        _porHuella.get(h).add(v.trim())
      }
    }
    _porHuellaVersion = _version
  }
  return [...(_porHuella.get(huella(String(textoCastellano == null ? '' : textoCastellano))) || [])]
}

// EL «LOCALE» DE UNA FECHA ESCRITA CON PALABRAS («miércoles, 4 de noviembre»): en castellano, 'es-ES' (lo de siempre); en otro
// idioma, el suyo, para que el día y el mes salgan en ese idioma (Intl los escribe). Solo para las fechas con nombre de día o de
// mes: las de números siguen el formato de la clínica. t609 · B-2259.
export function localeFechas() {
  return _estado.idioma === RESERVA ? 'es-ES' : localeDe(_estado.idioma)
}

// Las cabeceras de un calendario (['L', 'M', 'X'…], ['Lu', 'Ma'…]): en castellano, las de quien las escribe; en otro idioma, las
// del navegador. `empieza`: 1 si la lista empieza en lunes, 0 en domingo. `ancho`: 'narrow' (una letra) o 'short'. t609 · B-2259.
export function diasDeLaSemana(enCastellano, { empieza = 1, ancho = 'narrow' } = {}) {
  if (_estado.idioma === RESERVA) return enCastellano
  try {
    const f = new Intl.DateTimeFormat(localeDe(_estado.idioma), { weekday: ancho, timeZone: 'UTC' })
    // El 4-ene-1970 fue domingo.
    return enCastellano.map((_, i) => f.format(new Date(Date.UTC(1970, 0, 4 + empieza + i, 12))))
  } catch (_) { return enCastellano }
}

// EL NOMBRE DE UN PAÍS (su código ISO 3166-1 alfa-2: 'MA', 'DE'…) en el idioma activo, como lo dice el propio navegador (Intl, el
// CLDR): no hace falta traducir 249 nombres a doce idiomas. En castellano, el nombre que se pasa (el de la lista de siempre,
// utils/paises), tal cual. t609 · B-2259.
export function nombrePais(codigo, enCastellano) {
  if (_estado.idioma === RESERVA || !codigo) return enCastellano
  try {
    const n = new Intl.DisplayNames([localeDe(_estado.idioma)], { type: 'region' }).of(String(codigo).toUpperCase())
    return n && n !== String(codigo).toUpperCase() ? n : enCastellano
  } catch (_) { return enCastellano }
}

/** «Inglés», «english», «EN», «Русский» → 'en' / 'ru'; null si no es ninguno del catálogo. */
export function normalizarIdioma(texto) {
  return normalizarConCatalogo(texto, CATALOGO)
}

// Las lenguas de España que no tienen aún su traducción: quien tiene el móvil en ellas lee el castellano.
const DE_ESPANA = ['es', 'ca', 'gl', 'eu', 'ast', 'an', 'oc']

/**
 * El idioma de una pantalla del paciente: el que tiene guardado en su ficha si se puede elegir; si no, el de su
 * navegador si se puede elegir; una lengua de España, castellano; cualquier otro, inglés (lo pidió el dueño para el
 * pre-registro, B-1932). `navegador` es navigator.languages.
 */
export function idiomaParaPaciente({ guardado = null, navegador = [] } = {}) {
  const g = normalizarIdioma(guardado)
  if (g && esElegible(g)) return g
  const lista = (Array.isArray(navegador) ? navegador : [navegador]).filter((x) => typeof x === 'string' && x.trim())
    .map((x) => x.trim().toLowerCase().split(/[-_]/)[0])
  for (const l of lista) {
    if (esElegible(l)) return l
    if (DE_ESPANA.includes(l)) return RESERVA
  }
  if (!lista.length) return RESERVA
  return esElegible('en') ? 'en' : RESERVA
}

/** El idioma de una persona del equipo: el suyo, si no el de la clínica, si no el castellano (solo los elegibles). */
export function idiomaParaPersona({ persona = null, clinica = null } = {}) {
  if (persona && esElegible(persona)) return persona
  if (clinica && esElegible(clinica)) return clinica
  return RESERVA
}

// Solo para las guardias: vuelve al arranque (castellano, sin tipo, sin oyentes).
export function _reiniciarIdiomaParaPruebas() {
  _estado = { idioma: RESERVA, tipo: null, capas: [CAPA_ES] }
  _firma = firmaDe(_estado)
  _oyentes.clear()
}
